import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { IdValidation } from 'idmission-web-sdk';
import { useCallback, useState } from 'react';
import { Trans } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';

import { IconCell } from '@/components/Icon/IconCell';
import { ErrorMessage } from '@/components/Idmission/ErrorMessage';
import { IdmButton } from '@/components/Idmission/IdmButton.styled';
import { IdmCard } from '@/components/Idmission/IdmCard.styled';
import {
  IdmClassNames,
  IdmStyles,
} from '@/components/Idmission/Idmission.styled';
import { RejectedMessage } from '@/components/Idmission/RejectedMessage';
import {
  Form,
  FullPageOverlayBackground,
} from '@/components/Layout/Layout.styled';
import { UnifiedPage } from '@/components/Layout/Page/UnifiedPage';

import { ROUTE } from '@/constants/routes';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';
import { useIdmissionVerbiage } from '@/hooks/verification/useIdmissionVerbiage';
import { useIdmissionVerification } from '@/hooks/verification/useIdmissionVerification';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectUserHolderJointMinor } from '@/redux/user-data';

import { PROGRESS_STEPS } from '@/utils/progressSteps';
import { renderHTMLText } from '@/utils/string';
import { isSurinameUser } from '@/utils/user';

export function MinorIdmissionIDValidation() {
  const holderJointMinor = useAppSelector(selectUserHolderJointMinor);
  const user = useAppSelector(selectLoggedInUser);
  const isSuriname = isSurinameUser(user);
  const minorClientId = holderJointMinor?.clientId;
  const [isVerificationStarted, setIsVerificationStarted] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { t } = useTranslate();
  const { verbiage } = useIdmissionVerbiage();

  const onCompleted = () => {
    navigate(ROUTE.MINOR_BASIC_INFO, {
      replace: true,
    });
  };

  const {
    sessionId,
    isIDRejected,
    acknowledgeRejection,
    error,
    isProcessing,
    isCompleted,
    idCaptureRequirement,
    startVerification,
    handleComplete,
    handleAcknowledgeRejection,
    resetVerification,
  } = useIdmissionVerification({
    type: 'id',
    autoInit: false,
    minorClientId,
    initialAcknowledgeRejection: !!location.state?.skipRejection,
    onCompleted,
  });

  const handleStartVerification = useCallback(async () => {
    try {
      setIsVerificationStarted(true);
      setIsLoading(true);
      await startVerification();
    } catch (err) {
      console.error('Failed to start verification:', err);
    } finally {
      setIsLoading(false);
    }
  }, [startVerification]);

  const handleOnExit = useCallback(() => {
    resetVerification();
    setIsVerificationStarted(false);
  }, [resetVerification]);

  const renderVerificationContent = () => {
    if (!sessionId || isProcessing || isCompleted) {
      return (
        <FullPageOverlayBackground bgColor={theme.palette.background.paper}>
          <Box display="flex" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Box>
        </FullPageOverlayBackground>
      );
    }

    return (
      <FullPageOverlayBackground bgColor={theme.palette.background.paper}>
        <IdmStyles>
          <IdValidation
            sessionId={sessionId}
            idCaptureRequirement={idCaptureRequirement}
            onComplete={handleComplete}
            onExitCapture={handleOnExit}
            allowUploadingDocumentsFromStorage={false}
            classNames={IdmClassNames.idCapture}
            verbiage={verbiage.idCapture}
          />
        </IdmStyles>
      </FullPageOverlayBackground>
    );
  };

  if (isIDRejected && !acknowledgeRejection) {
    return (
      <RejectedMessage
        isIDRejected={isIDRejected}
        isSelfieRejected={false}
        isLivenessRejected={false}
        isMinor
        onAcknowledge={handleAcknowledgeRejection}
      />
    );
  }

  if (error) {
    return <ErrorMessage error={error} onAcknowledge={handleOnExit} />;
  }

  return (
    <UnifiedPage
      hasDarkBackground
      bgColor={(theme: Theme) => theme.palette.primaries[900]}
      progressProps={{
        ...PROGRESS_STEPS.livenessVerificationStep,
        activeColor: (theme: Theme) => theme.palette.primaries[400],
        inactiveColor: (theme: Theme) => theme.palette.grey[300],
      }}
      hasBackToLoginButton
      scrollable
    >
      <Typography variant="h1" mt={3}>
        {t('verification.idmission.child.title')}
      </Typography>
      <Form sx={{ alignSelf: 'flex-start' }}>
        <Typography variant="body2">
          {renderHTMLText(
            t(
              `verification.idmission.gettingStartedInstruction.${idCaptureRequirement}`,
            ),
          )}
        </Typography>
      </Form>

      {!isVerificationStarted ? (
        <>
          <IdmCard maxWidth={400}>
            <IconCell
              name="camera"
              color={(theme: Theme) => theme.palette.common.white}
              bgColor={(theme: Theme) => theme.palette.primaries[500]}
              size={80}
              iconSize={40}
            />
            <IdmButton
              onClick={handleStartVerification}
              variant="contained"
              disabled={isLoading}
              size="large"
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('verification.idmission.idCapture.captureWithCameraText')
              )}
            </IdmButton>
          </IdmCard>

          <Stack mt={2} direction={'column'} maxWidth={400}>
            <Typography variant="body2">
              {isSuriname ? (
                <Trans
                  i18nKey="verification.verifyMinorID.suriname.footerText"
                  components={{ bold: <strong /> }}
                />
              ) : (
                t('verification.verifyMinorID.footerText')
              )}
            </Typography>
            <Button
              variant="outlined"
              component={Link}
              to={ROUTE.VERIFY_MINOR_BIRTH_CERTIFICATE}
              fullWidth
              sx={{ borderRadius: 2 }}
            >
              {t('verification.verifyMinorID.verifyWithBirthCertificate')}
            </Button>
            {isSuriname && (
              <Button
                variant="outlined"
                component={Link}
                to={ROUTE.VERIFY_MINOR_FAMILY_BOOK}
                fullWidth
                sx={{ borderRadius: 2, mt: 1 }}
              >
                {t('verification.verifyMinorID.verifyWithFamilyBook')}
              </Button>
            )}
          </Stack>
        </>
      ) : (
        renderVerificationContent()
      )}
    </UnifiedPage>
  );
}
